import { DateField, Field, Image, ImageField, RichText, Text, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';

import { ComponentProps } from 'lib/ComponentProps';

import { parseIsoDate } from 'src/mappers/fields/parseDate';
import { mapFilterGroup } from 'src/mappers/models/filterGroupMapper';

import { ExtendedItem } from 'src/types/ExtendedItem';
import { TagOption } from 'src/types/FilterGroup';

import { isClinicalTopic } from 'src/utils/isTemplate';
import { searchContentQueryString } from 'src/utils/searchContentQueryString';

import WildCardFields from 'src/components/contentOverview/WildCardFields';

import CalendarSvg from 'public/icons/calendar.svg';

import { BackToSearch } from '../_BackToSearch/_BackToSearch';

import styles from './ContentSummary.module.css';

type ContentSummaryProps = ComponentProps & {
	fields: WildCardFields & {
		Abstract: Field<string>;
		Image: ImageField;
		'Background Image': ImageField;
		PageTitle: Field<string>;
		StartDateText: Field<string>;
		EndDateText: Field<string>;
		Tags: ExtendedItem &
			[
				{
					fields: {
						Title: Field<string>;
					};
				}
			];
		'Start Date': Field<string>;
		'End Date': Field<string>;
		SubjectLinkPrependedText: Field<string>;
	};
};

export default withDatasourceCheck()<ContentSummaryProps>(ContentSummary);

function ContentSummary(props: ContentSummaryProps): JSX.Element {
	if (!isClinicalTopic(props.fields?.WildCardContentTemplateId)) {
		return <></>;
	}

	const startDateField = props.fields['Start Date'];
	const startDate = startDateField ? parseIsoDate(startDateField.value) : undefined;
	const displayStartDate = startDate && startDate.getFullYear() > 1900;

	const endDateField = props.fields['End Date'];
	const endDate = endDateField ? parseIsoDate(endDateField.value) : undefined;
	const displayEndDate = endDate && endDate.getFullYear() > 1900;

	const filterGroups = mapFilterGroup(props.fields?.WildCardConfig?.FilterGroups ?? []) || [];

	const optionsToDisplay: TagOption[] = filterGroups
		.flatMap((filter) => {
			return filter.options.map((option) => {
				const opt: TagOption = {
					filterName: filter.dtoPropertyName,
					optionId: option.title.replaceAll(' ', '-') + '_' + option.id,
					optionTitle: option.title,
				};

				return opt;
			});
		})
		.filter((tag) => {
			if (tag.optionTitle && props.fields.Tags) {
				if (props.fields.Tags.flatMap((t) => t.fields.Title?.value).indexOf(tag.optionTitle) > -1) {
					return true;
				}
			}
			return false;
		});

	const dateStatus = (text: Field<string>, dateField: Field<string>, endDate?: boolean) => {
		return (
			<span className={styles.dateStatus}>
				{endDate && <>{' - '}</>}
				<Text field={text} />{' '}
				<DateField
					field={dateField}
					render={(date: Date) =>
						date &&
						date.toLocaleString('en-GB', {
							day: 'numeric',
							month: 'numeric',
							year: 'numeric',
							hour: '2-digit',
							minute: '2-digit',
							hour12: true,
						})
					}
				/>
			</span>
		);
	};

	return (
		<section className={`${styles.section}`} data-item-id={props.fields.WildCardContentId}>
			<BackToSearch />

			<div className={`${styles.snippet} maxWidth`}>
				{(props.fields.Image || props.fields['Background Image']) && (
					<div className={styles.imageContainer}>
						<Image className={styles.image} field={props.fields.Image ?? props.fields['Background Image']} loading="lazy" />
					</div>
				)}

				<div className={styles.column}>
					<RichText className={styles.abstract} field={props.fields.Abstract} />

					{optionsToDisplay && optionsToDisplay.length > 0 && (
						<p className={styles.subject}>
							<Text field={props.fields.SubjectLinkPrependedText} />
							{optionsToDisplay.map((tag, index) => {
								return (
									<div className={styles.tag} key={index}>
										<a
											href={searchContentQueryString(
												tag.filterName,
												props.fields?.WildCardConfig?.SearchListingWildcardPage?.value?.href,
												tag.optionId
											)}
											className={`${styles.subheadingLink} link linkOffset linkHoverWeighted`}
										>
											{tag.optionTitle && tag.optionTitle.replace(/^\s+|\s+$/g, '')}
										</a>
										{index + 1 < optionsToDisplay.length ? ', ' : ''}
									</div>
								);
							})}
						</p>
					)}

					{displayStartDate || displayEndDate ? (
						<div className={styles.dateRow}>
							<CalendarSvg className={styles.icon} />

							{startDateField && dateStatus(props.fields.StartDateText, startDateField)}
							{endDateField && dateStatus(props.fields.EndDateText, endDateField, true)}
						</div>
					) : null}
				</div>
			</div>
		</section>
	);
}
